import { toast } from 'react-toastify';

import { Alert, Confirm } from '../components/atoms';
import { HomographyFilenames } from '../constants';
import { fleetStages } from '../enums';
import { Fleet, OTAFileV2, OTAResponse } from '../interfaces';
import { CalibrationPairs, CalibrationType, HomographyMatrixPair, HomographyRequest } from '../interfaces/homography';
import { AppState } from '../reducers';
import { ActiveMapMarker, CalibrationPairsState, HomographyState } from '../reducers/homography/homographyState';
import { DevicesService } from '../services';
import { CameraGpsHomographyCheck } from '../utils';
import { latLongDistance } from '../utils/homographyHelpers';

export enum HomographyActions {
  UPDATE_LPR_CONTEXT_POINTS = 'UPDATE_LPR_CONTEXT_POINTS',
  UPDATE_LPR_POINTS = 'UPDATE_LPR_POINTS',
  UPDATE_NIGHT_LPR_CONTEXT_POINTS = 'UPDATE_NIGHT_LPR_CONTEXT_POINTS',
  UPDATE_NIGHT_LPR_POINTS = 'UPDATE_NIGHT_LPR_POINTS',
  UPDATE_MAP_CONTEXT_POINTS = 'UPDATE_MAP_CONTEXT_POINTS',
  UPDATE_MAP_POINTS = 'UPDATE_MAP_POINTS',
  UPDATE_MAP_MARKERS = 'UPDATE_MAP_MARKERS',
  DELETE_MAP_MARKERS = 'DELETE_MAP_MARKERS',
  CALCULATING_HOMOGRAPHY = 'CALCULATING_HOMOGRAPHY',
  LPR_MODIFIED = 'LPR_MODIFIED',
  MAP_MODIFIED = 'MAP_MODIFIED',
  NIGHT_LPR_MODIFIED = 'NIGHT_LPR_MODIFIED',
  UPDATE_CALCULATED_HOMOGRAPHY = 'UPDATE_CALCULATED_HOMOGRAPHY',
  DELETE_CALCULATED_HOMOGRAPHY = 'DELETE_CALCULATED_HOMOGRAPHY',
}

export enum HomographyCamera {
  CONTEXT = 'CONTEXT',
  LPR = 'LPR',
  MAP = 'MAP',
}

const setIsSubmitting = (isSubmitting: boolean) => {
  return {
    type: HomographyActions.CALCULATING_HOMOGRAPHY,
    payload: isSubmitting,
  };
};

export const deleteMapMarker = (markerIndex: number) => {
  return (dispatch: any, getState: () => AppState) => {
    const { activeMarkers } = getState().homography.mapPairs;
    const { points } = getState().homography.mapPairs.imagePairs.imageTwo;
    const partnerPoints = getState().homography.mapPairs.imagePairs.imageOne.points;
    const filteredMarkers = activeMarkers.filter((x) => x[0] !== markerIndex);
    const filteredPoints = points.filter((x) => x[0] !== markerIndex);
    const updatedMarkers = filteredMarkers.map((x) => {
      if (x[0] < markerIndex) {
        return x;
      }
      x[0] -= 1;
      return x;
    });
    const updatedPoints = filteredPoints.map((x) => {
      if (x[0] < markerIndex) {
        return x;
      }
      x[0] -= 1;
      return x;
    });
    dispatch(updatePoints(updatedPoints, HomographyCamera.MAP, CalibrationType.MAP));
    dispatch({
      type: HomographyActions.UPDATE_MAP_MARKERS,
      payload: updatedMarkers,
    });

    if (updatedPoints.length === 0 && partnerPoints.length === 0) {
      dispatch(setModified(false, CalibrationType.MAP));
    }
  };
};

export const deletePoint = (index: number, calibrationType: CalibrationType, cameraType: HomographyCamera) => {
  return (dispatch: any, getState: () => AppState) => {
    const activePoints = getPoints(getState(), calibrationType, cameraType);
    const filteredPoints = activePoints.filter((x) => x[0] !== index);
    const updatedPoints = filteredPoints.map((x) => {
      if (x[0] < index) {
        return x;
      }
      x[0] -= 1;
      return x;
    });
    dispatch(updatePoints(updatedPoints, cameraType, calibrationType));
    if (calibrationType === CalibrationType.MAP && updatedPoints.length === 0) {
      dispatch(setModified(false, CalibrationType.MAP));
    }
  };
};

export const updateMapPoints = (center: google.maps.LatLng, heading: number) => {
  return (dispatch: any, getState: () => AppState) => {
    const { activeMarkers } = getState().homography.mapPairs;
    const points = activeMarkers.map((x) => [x[0], [...latLongDistance(center, x[1], heading), 0]]);
    dispatch(updatePoints(points, HomographyCamera.MAP, CalibrationType.MAP));
  };
};

export const updateMapMarker = (marker: ActiveMapMarker) => {
  return (dispatch: any, getState: () => AppState) => {
    const { activeMarkers } = getState().homography.mapPairs;
    if (marker[0] >= activeMarkers.length) {
      dispatch({
        type: HomographyActions.UPDATE_MAP_MARKERS,
        payload: [...activeMarkers, marker],
      });
    } else {
      const updatedMarkers = activeMarkers.map((x) => (x[0] == marker[0] ? marker : x));
      dispatch({
        type: HomographyActions.UPDATE_MAP_MARKERS,
        payload: updatedMarkers,
      });
    }
  };
};

const deleteMapMarkers = () => {
  return { type: HomographyActions.DELETE_MAP_MARKERS };
};

export const updatePoints = (points: any[], cameraType: HomographyCamera, calibrationType: CalibrationType) => {
  return {
    type: getUpdatePointsActionType(cameraType, calibrationType),
    payload: points,
  };
};

export const setModified = (modified: boolean, calibrationType: CalibrationType) => {
  const calibrationTypeMap = new Map<string, string>([
    [CalibrationType.LPR, HomographyActions.LPR_MODIFIED],
    [CalibrationType.NIGHTLPR, HomographyActions.NIGHT_LPR_MODIFIED],
    [CalibrationType.MAP, HomographyActions.MAP_MODIFIED],
  ]);
  return {
    type: calibrationTypeMap.get(calibrationType),
    payload: modified,
  };
};

export const resetAllHomography = () => {
  return (dispatch: any, getState: () => AppState) => {
    dispatch(setModified(false, CalibrationType.LPR));
    dispatch(setModified(false, CalibrationType.NIGHTLPR));
    dispatch(setModified(false, CalibrationType.MAP));
    dispatch(deleteCaluclatedHomography(CalibrationType.LPR));
    dispatch(deleteCaluclatedHomography(CalibrationType.NIGHTLPR));
    dispatch(deleteCaluclatedHomography(CalibrationType.MAP));
    dispatch(resetHomography(CalibrationType.LPR));
    dispatch(resetHomography(CalibrationType.NIGHTLPR));
    dispatch(resetHomography(CalibrationType.MAP));
  };
};

export const resetHomography = (calibrationType: CalibrationType) => {
  return (dispatch: any) => {
    if (calibrationType === CalibrationType.MAP) {
      dispatch({
        type: HomographyActions.UPDATE_MAP_MARKERS,
        payload: [],
      });
      dispatch(updateMapPoints(null, null));
      dispatch(deleteMapMarkers);
      dispatch(updatePoints([], HomographyCamera.CONTEXT, calibrationType));
    } else {
      dispatch(updatePoints([], HomographyCamera.CONTEXT, calibrationType));
      dispatch(updatePoints([], HomographyCamera.LPR, calibrationType));
    }
    dispatch(setModified(false, calibrationType));
    dispatch(deleteCaluclatedHomography(calibrationType));
  };
};

export const calculateHomography = (deviceId?: string) => {
  return async (dispatch: any, getState: () => AppState) => {
    const homographyState = getState().homography;
    const calibrationData = buildRequestData(homographyState);

    const requestBody: HomographyRequest = {
      deviceId: deviceId ?? '0',
      imagePairs: calibrationData,
    };
    try {
      const calculateResponse = await DevicesService.calculateHomography(deviceId ?? '0', requestBody);
      calculateResponse?.transformMatrices?.forEach(({ calibrationType, transformMatrix }, i) => {
        dispatch(updateCalculatedHomography(calibrationType, transformMatrix));
      });
    } catch (e) {
      console.error(e);
    }
  };
};

export const calculateAndSaveHomography = (deviceId: string, deviceFiles: OTAFileV2[], fleet: Fleet, event: Event) => {
  return async (dispatch: any, getState: () => AppState) => {
    const homographyState = getState().homography;

    dispatch(setIsSubmitting(true));

    try {
      const calculatedGPSHomography = homographyState.mapPairs.calculatedHomography;

      if (homographyState.mapPairs.modified) {
        if (!calculatedGPSHomography) {
          return new Alert(
            "The homography check for calculated homograpy didn't succeed. Please set it or clear and try again.",
          );
        }
      }

      if (homographyState.mapPairs.modified) {
        try {
          const pythonCompatibleFormat = [
            [calculatedGPSHomography[0], calculatedGPSHomography[1], calculatedGPSHomography[2]],
            [calculatedGPSHomography[3], calculatedGPSHomography[4], calculatedGPSHomography[5]],
            [calculatedGPSHomography[6], calculatedGPSHomography[7], calculatedGPSHomography[8]],
          ];

          const tool = new CameraGpsHomographyCheck();
          const gpsHomographyCheckResult = tool.runCameraGpsHomographyCheck(pythonCompatibleFormat, event);

          if (gpsHomographyCheckResult.result !== 'SUCCESS') {
            const isProd = fleet.stage === fleetStages.PRODUCTION;
            if (isProd) {
              await new Alert(gpsHomographyCheckResult.message!).promise;
              return false;
            }
            const message = `${gpsHomographyCheckResult.mssage} Do you want to proceed anyway?`;
            const res = await new Confirm(message, 'Continue', 'Cancel').promise;
            if (!res) {
              return false;
            }
          }
        } catch (e) {
          console.error('CameraGpsHomographyCheck', e);
        }
      }

      const promises: Promise<OTAResponse>[] = [];

      if (homographyState.lPRPairs.modified) {
        const filename = HomographyFilenames[CalibrationType.LPR];

        const homographyFile = deviceFiles.data?.find((file) => file.destination === filename);
        if (homographyFile) {
          await DevicesService.deleteOtaFile(deviceId, homographyFile.device_file_id);
        }

        const promise = DevicesService.calibrateHomography(
          deviceId,
          homographyState.lPRPairs.calculatedHomography,
          CalibrationType.LPR,
        );
        promises.push(promise);
        promise.then((res) => {
          if (res?.files) {
            toast.success('LPR calibration saved!');
          }
        });
      }

      if (homographyState.nightLPRPairs.modified) {
        const filename = HomographyFilenames[CalibrationType.NIGHTLPR];

        const homographyFile = deviceFiles.data?.find((file) => file.destination === filename);
        if (homographyFile) {
          await DevicesService.deleteOtaFile(deviceId, homographyFile.device_file_id);
        }

        const promise = DevicesService.calibrateHomography(
          deviceId,
          homographyState.nightLPRPairs.calculatedHomography,
          CalibrationType.NIGHTLPR,
        );
        promises.push(promise);
        promise.then((res) => {
          if (res?.files) {
            toast.success('Night LPR calibration saved!');
          }
        });
      }

      if (homographyState.mapPairs.modified) {
        const filename = HomographyFilenames[CalibrationType.MAP];

        const homographyFile = deviceFiles.data?.find((file) => file.destination === filename);
        if (homographyFile) {
          await DevicesService.deleteOtaFile(deviceId, homographyFile.device_file_id);
        }

        const promise = DevicesService.calibrateHomography(
          deviceId,
          homographyState.mapPairs.calculatedHomography,
          CalibrationType.MAP,
        );
        promises.push(promise);
        promise.then((res) => {
          if (res?.files) {
            toast.success('GPS calibration saved!');
          }
        });
      }

      await Promise.all(promises);

      dispatch(setModified(false, CalibrationType.LPR));
      dispatch(setModified(false, CalibrationType.NIGHTLPR));
      dispatch(setModified(false, CalibrationType.MAP));
      dispatch(setIsSubmitting(false));
    } catch (e) {
      toast.error(e);
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };
};

export const updateCalculatedHomography = (calibrationType: CalibrationType, transformMatrix: Float32Array) => {
  return {
    type: HomographyActions.UPDATE_CALCULATED_HOMOGRAPHY,
    payload: { calibrationType, transformMatrix } as HomographyMatrixPair,
  };
};

export const deleteCaluclatedHomography = (calibtationType: CalibrationType) => {
  return {
    type: HomographyActions.DELETE_CALCULATED_HOMOGRAPHY,
    payload: calibtationType,
  };
};

const getUpdatePointsActionType = (
  cameraType: HomographyCamera,
  calibrationType: CalibrationType,
): HomographyActions => {
  switch (calibrationType) {
    case CalibrationType.LPR:
      return getLPRUpateActionType(cameraType);
    case CalibrationType.NIGHTLPR:
      return getNightLPRUpateActionType(cameraType);
    case CalibrationType.MAP:
      return getMAPUpateActionType(cameraType);
  }
};

const getMAPUpateActionType = (cameraType: HomographyCamera) => {
  switch (cameraType) {
    case HomographyCamera.CONTEXT:
      return HomographyActions.UPDATE_MAP_CONTEXT_POINTS;
    case HomographyCamera.MAP:
      return HomographyActions.UPDATE_MAP_POINTS;
  }
};

const getLPRUpateActionType = (cameraType: HomographyCamera) => {
  switch (cameraType) {
    case HomographyCamera.CONTEXT:
      return HomographyActions.UPDATE_LPR_CONTEXT_POINTS;
    case HomographyCamera.LPR:
      return HomographyActions.UPDATE_LPR_POINTS;
    default:
  }
};

const getNightLPRUpateActionType = (cameraType: HomographyCamera) => {
  switch (cameraType) {
    case HomographyCamera.CONTEXT:
      return HomographyActions.UPDATE_NIGHT_LPR_CONTEXT_POINTS;
    case HomographyCamera.LPR:
      return HomographyActions.UPDATE_NIGHT_LPR_POINTS;
    default:
  }
};

const buildRequestData = (state: HomographyState): CalibrationPairs[] => {
  const calibrationPairs: CalibrationPairs[] = [];
  if (state.lPRPairs.modified) {
    const pair = buildImagePair(state.lPRPairs, CalibrationType.LPR);
    calibrationPairs.push(pair);
  }
  if (state.nightLPRPairs.modified) {
    const pair = buildImagePair(state.nightLPRPairs, CalibrationType.NIGHTLPR);
    calibrationPairs.push(pair);
  }
  if (state.mapPairs.modified) {
    const pair = buildImagePair(state.mapPairs, CalibrationType.MAP);
    calibrationPairs.push(pair);
  }
  return calibrationPairs;
};

const buildImagePair = (pair: Partial<CalibrationPairsState>, calibrationType: CalibrationType): CalibrationPairs => {
  let imagePair = {};
  if (calibrationType === CalibrationType.MAP) {
    imagePair = {
      calibrationType,
      imagePairs: {
        imageOne: {
          name: 'Context',
          points: pair.imagePairs.imageOne.points.map((x) => [x[0], scalePoints(x[1], pair.contextScale)]),
        },
        imageTwo: {
          name: 'MAP',
          points: pair.imagePairs.imageTwo.points,
        },
      },
    };
  } else {
    imagePair = {
      calibrationType,
      imagePairs: {
        imageOne: {
          name: 'Context',
          points: pair.imagePairs.imageOne.points.map((x) => [x[0], scalePoints(x[1], pair.contextScale)]),
        },
        imageTwo: {
          name: 'LPR',
          points: pair.imagePairs.imageTwo.points.map((x) => [x[0], scalePoints(x[1], pair.lprScale)]),
        },
      },
    };
  }
  return imagePair as CalibrationPairs;
};

const scalePoints = (points: number[], scale: number): [number, number, number] => {
  return [points[0] / scale, points[1] / scale, 0];
};

const getPoints = (state: AppState, calibrationType: CalibrationType, cameraType: HomographyCamera) => {
  switch (calibrationType) {
    case CalibrationType.MAP:
      switch (cameraType) {
        case HomographyCamera.CONTEXT:
          return state.homography.mapPairs.imagePairs.imageOne.points;
        case HomographyCamera.MAP:
          return state.homography.mapPairs.imagePairs.imageTwo.points;
      }
    case CalibrationType.LPR:
      switch (cameraType) {
        case HomographyCamera.CONTEXT:
          return state.homography.lPRPairs.imagePairs.imageOne.points;
        case HomographyCamera.LPR:
          return state.homography.lPRPairs.imagePairs.imageTwo.points;
      }
    case CalibrationType.NIGHTLPR:
      switch (cameraType) {
        case HomographyCamera.CONTEXT:
          return state.homography.nightLPRPairs.imagePairs.imageOne.points;
        case HomographyCamera.LPR:
          return state.homography.nightLPRPairs.imagePairs.imageTwo.points;
      }
  }
};
